
import { IntentResult, MatchResult, GuidanceResult, IntentConfig, Intent, EntityConfig, FallbackConfig } from "./intent-result";
import { isExplicitArbeidsplassenQuery } from "../services/arbeidsplassen-client-service";
import { isExplicitUtdanningQuery } from "../services/utdanning-explicit-checker";

/**
 * STRICT: Enhanced intent matching with explicit detection requirements
 */
class IntentMatcher {
  matchIntent(query: string): IntentResult {
    const lowerQuery = query.toLowerCase();
    
    console.log(`[STRICT-INTENT-MATCHER] 🎯 Analyzing query: "${query}"`);
    
    // PRIORITY 1: Explicit Enhetsregisteret detection
    if (this.isExplicitEnhetsregisterQuery(lowerQuery)) {
      console.log('[STRICT-INTENT-MATCHER] 🏛️ EXPLICIT ENHETSREGISTER detected');
      return {
        intent: 'search_brreg_explicit',
        handler: 'brreg',
        confidence: 0.95,
        entities: { explicit_mention: true }
      };
    }
    
    // PRIORITY 2: Explicit Arbeidsplassen.no detection
    if (isExplicitArbeidsplassenQuery(query)) {
      console.log('[STRICT-INTENT-MATCHER] 🏢 EXPLICIT ARBEIDSPLASSEN detected');
      return {
        intent: 'search_arbeidsplassen_explicit',
        handler: 'arbeidsplassen',
        confidence: 0.95,
        entities: { explicit_mention: true }
      };
    }
    
    // PRIORITY 3: STRICT Explicit Utdanning.no detection
    if (isExplicitUtdanningQuery(query)) {
      console.log('[STRICT-INTENT-MATCHER] 🎓 EXPLICIT UTDANNING.NO detected');
      return {
        intent: 'search_utdanning_explicit',
        handler: 'utdanning',
        confidence: 0.95,
        entities: { explicit_mention: true }
      };
    }
    
    // LOWER PRIORITY: General patterns (without explicit mentions)
    if (this.isJobQuery(lowerQuery)) {
      console.log('[STRICT-INTENT-MATCHER] 📋 General job query (no explicit mention)');
      return {
        intent: 'search_jobs_general',
        handler: 'arbeidsplassen',
        confidence: 0.6,
        entities: { general_query: true }
      };
    }
    
    if (this.isBusinessQuery(lowerQuery)) {
      console.log('[STRICT-INTENT-MATCHER] 🏭 General business query');
      return {
        intent: 'search_business_general',
        handler: 'brreg',
        confidence: 0.6,
        entities: { general_query: true }
      };
    }
    
    // IMPORTANT: General education terms no longer trigger Utdanning.no
    console.log('[STRICT-INTENT-MATCHER] ❌ No explicit integration mention - providing guidance');
    
    return {
      shouldProvideGuidance: true,
      guidanceType: 'general',
      confidence: 0.3
    };
  }
  
  private isExplicitEnhetsregisterQuery(lowerQuery: string): boolean {
    const enhetsregisterTerms = [
      'enhetsregister', 'enhetsregisteret', 'brønnøysund', 'brreg',
      'søk enhetsregisteret', 'finn i enhetsregisteret'
    ];
    
    return enhetsregisterTerms.some(term => lowerQuery.includes(term));
  }
  
  private isJobQuery(lowerQuery: string): boolean {
    const jobTerms = ['jobb', 'stilling', 'ledige', 'ansette'];
    return jobTerms.some(term => lowerQuery.includes(term));
  }
  
  private isBusinessQuery(lowerQuery: string): boolean {
    const businessTerms = ['bedrift', 'firma', 'selskap', 'butikk'];
    return businessTerms.some(term => lowerQuery.includes(term));
  }
}

export const intentMatcher = new IntentMatcher();

// Export enhanced functions for compatibility
export const enhancedMightBeDataQuery = (query: string): boolean => {
  const result = intentMatcher.matchIntent(query);
  return 'handler' in result || 'shouldProvideGuidance' in result;
};

export const enhancedDetectDatabase = (query: string): string => {
  const result = intentMatcher.matchIntent(query);
  if ('handler' in result) {
    return result.handler;
  }
  return 'brreg'; // Default fallback
};

export const shouldProvideGuidance = (query: string): boolean => {
  const result = intentMatcher.matchIntent(query);
  return 'shouldProvideGuidance' in result && result.shouldProvideGuidance;
};

// Export types
export type { IntentResult, MatchResult, GuidanceResult, IntentConfig, Intent, EntityConfig, FallbackConfig };
